import AddIcon from '@mui/icons-material/Add';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { Button, SxProps, Theme, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Link, useNavigate, useSearchParams } from 'react-router';

import { ConfirmModal } from '~/components/ConfirmModal';
import { EmptyData } from '~/components/EmptyData';
import { Pagination } from '~/components/Pagination';
import { SearchInput } from '~/components/SearchInput';
import { ADMIN_ROUTES } from '~/constants/routes';
import { useNotification } from '~/hooks/useNotification';
import { useDeleteUserMutation, useGetUsersQuery } from '~/services/rtkQuery/admin';
import { formatUserPosition, formatUserRole } from '~/utils/formats';
import { getCurrentPageInfo, getTotalPagesCount } from '~/utils/pagination';

import styles from './styles.module.scss';

const SEARCH_INPUT_SX: SxProps<Theme> = { mr: '12px', width: '344px' };
const FETCH_LIMIT = 10;

export const UsersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams.get('search') || '';
  const currentPage = +(searchParams.get('page') || 1);
  const navigate = useNavigate();
  const [deleteConfirmModalVisible, setDeleteConfirmModalVisible] = useState(false);
  const { showNotification, handleApiError } = useNotification();
  const activeUserId = useRef<number | null>(null);
  const getUsersQuery = useGetUsersQuery({
    name: searchValue.trim(),
    pageNum: currentPage,
    perPage: FETCH_LIMIT,
  });
  const [deleteUserMutation, { isLoading: isLoadingDeleteUserMutation }] = useDeleteUserMutation();

  useEffect(() => {
    if (currentPage > 1 && getUsersQuery.data && getUsersQuery.data.result.data.length === 0) {
      setSearchParams(prevValue => {
        prevValue.set(
          'page',
          getTotalPagesCount({
            totalItemsCount: getUsersQuery.data?.result.total || 0,
            fetchLimit: FETCH_LIMIT,
          }).toString(),
        );

        return prevValue;
      });
    }
  }, [currentPage, getUsersQuery.data, getUsersQuery.data?.result.data.length, setSearchParams]);

  const handleSearchValueChange = useCallback(
    (value: string) => {
      setSearchParams(prevValue => {
        prevValue.set('search', value);
        prevValue.set('page', '1');

        return prevValue;
      });
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams(prevValue => {
        prevValue.set('page', page.toString());

        return prevValue;
      });
    },
    [setSearchParams],
  );

  const handleUserClick = useCallback(
    (id: number) => {
      navigate(generatePath(ADMIN_ROUTES.EDIT_USER, { id: id.toString() }));
    },
    [navigate],
  );

  const showDeleteConfirmModal = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
      event.stopPropagation();

      activeUserId.current = id;

      setDeleteConfirmModalVisible(true);
    },
    [],
  );

  const hideDeleteConfirmModal = useCallback(() => {
    setDeleteConfirmModalVisible(false);
  }, []);

  const handleUserDelete = useCallback(async () => {
    try {
      if (activeUserId.current) {
        const response = await deleteUserMutation({ id: activeUserId.current }).unwrap();

        showNotification({
          type: 'success',
          title: response.title,
          message: response.message,
        });
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      hideDeleteConfirmModal();
    }
  }, [deleteUserMutation, handleApiError, hideDeleteConfirmModal, showNotification]);

  const totalPages = useMemo(() => {
    return getTotalPagesCount({
      totalItemsCount: getUsersQuery.data?.result.total || 0,
      fetchLimit: FETCH_LIMIT,
    });
  }, [getUsersQuery.data?.result.total]);

  return (
    <div className={styles.container}>
      <div className={styles.container__heading}>
        <Typography color="var(--text-2-color)" fontWeight="700" variant="subtitle1">
          Users
        </Typography>
        <div className={styles['container__right-column']}>
          <SearchInput
            initialValue={searchValue}
            placeholder="Search here..."
            sx={SEARCH_INPUT_SX}
            onChange={handleSearchValueChange}
          />
          <Link to={ADMIN_ROUTES.CREATE_USER}>
            <Button
              classes={{ startIcon: styles['container__new-button-icon'] }}
              color="secondary"
              size="small"
              startIcon={<AddIcon sx={{ width: '24px', height: '24px' }} />}
              sx={{ width: '112px', paddingLeft: '6px', paddingRight: '6px' }}
            >
              New User
            </Button>
          </Link>
        </div>
      </div>
      {getUsersQuery.isFetching ||
      !getUsersQuery.data ||
      getUsersQuery.data.result.data.length === 0 ? (
        <EmptyData
          className={styles['container__empty-data']}
          description="No users available"
          loading={getUsersQuery.isFetching}
        />
      ) : (
        <>
          <table className={styles.container__table}>
            <thead>
              <tr>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    First Name
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Last Name
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Email
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Position
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Role
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {getUsersQuery.data?.result.data.map(user => (
                <tr
                  className={user.protected ? styles['container__protected-row'] : undefined}
                  key={user.id}
                  onClick={user.protected ? undefined : () => handleUserClick(user.id)}
                >
                  <td>
                    <Typography
                      color="text.primary"
                      fontWeight="500"
                      lineHeight="16px"
                      variant="body1"
                    >
                      {user.first_name}
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      color="text.primary"
                      fontWeight="500"
                      lineHeight="16px"
                      variant="body1"
                    >
                      {user.last_name}
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      color="text.primary"
                      fontWeight="500"
                      lineHeight="16px"
                      variant="body1"
                    >
                      {user.email}
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      color="text.primary"
                      fontWeight="500"
                      lineHeight="16px"
                      variant="body1"
                    >
                      {formatUserPosition(user.position)}
                    </Typography>
                  </td>
                  <td>
                    <div>
                      <Typography
                        color="text.primary"
                        fontWeight="500"
                        lineHeight="16px"
                        variant="body1"
                      >
                        {formatUserRole(user.role)}
                      </Typography>
                      {!user.protected && (
                        <button
                          aria-label="Delete"
                          title="Delete"
                          type="button"
                          onClick={event => showDeleteConfirmModal(event, user.id)}
                        >
                          <DeleteForeverOutlinedIcon
                            sx={{
                              color: 'var(--gray-6-color)',
                              ':hover': { color: 'var(--gray-2-color)' },
                            }}
                          />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.container__footer}>
            <Typography color="var(--gray-2-color)" lineHeight="14px" variant="subtitle2">
              {getCurrentPageInfo({
                fetchLimit: FETCH_LIMIT,
                currentPage,
                currentPageItemsCount: getUsersQuery.data?.result.data.length || 0,
                totalItemsCount: getUsersQuery.data?.result.total || 0,
              })}
            </Typography>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={handlePageChange}
            />
          </div>
        </>
      )}
      <ConfirmModal
        description="Are you sure you want to delete this user?"
        loading={isLoadingDeleteUserMutation}
        open={deleteConfirmModalVisible}
        title="Do you want to delete?"
        onCancel={hideDeleteConfirmModal}
        onConfirm={handleUserDelete}
      />
    </div>
  );
};
